import type { CampaignFilters, CampaignObjectiveKey } from '@crm/shared';
import { Button, Input, Modal, Spinner } from '@crm/ui';
import { ChevronLeft, ChevronRight, ExternalLink, Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '@/components';
import { useDebounce } from '@/hooks';
import { CUSTOMERS_ROUTES } from '@/modules/customers/api';
import { useCampaignPreview } from '../api';

interface RecipientsModalProps {
  filters: CampaignFilters;
  objective: CampaignObjectiveKey;
  onClose: () => void;
}

/** Dez cabem na tela do celular sem virar rolagem infinita. */
const POR_PAGINA = 10;

/**
 * Quem vai receber, nome a nome. Fora daqui so o total aparece: uma base de
 * milhares de clientes nao cabe - nem ajuda - dentro do formulario.
 */
export function RecipientsModal({ filters, objective, onClose }: RecipientsModalProps) {
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const termo = useDebounce(busca, 350);

  const preview = useCampaignPreview(filters, objective, pagina, POR_PAGINA, termo);
  const paginas = preview.data?.meta.totalPages ?? 1;
  const total = preview.data?.total ?? 0;

  function procura(valor: string) {
    setBusca(valor);
    setPagina(1);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Destinatarios"
      subtitle={termo ? `${total} encontrados` : `${total} clientes atendem aos filtros`}
      size="medium"
    >
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Buscar por nome ou telefone"
          value={busca}
          onChange={(event) => procura(event.target.value)}
        />
      </div>

      {preview.isPending ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : preview.data?.sample.length === 0 ? (
        <EmptyState title="Ninguem com esse nome ou numero" />
      ) : (
        <ul className="divide-y divide-border">
          {preview.data?.sample.map((pessoa) => (
            <li key={pessoa.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{pessoa.name}</p>
                <p className="truncate text-xs text-muted-foreground">{pessoa.phone}</p>
              </div>

              {/* Em outra aba: conferir um cadastro nao pode custar o publico
                  que estava sendo montado. */}
              <Button variant="ghost" size="sm" asChild className="shrink-0">
                <Link
                  to={CUSTOMERS_ROUTES.DETAIL_PATTERN.replace(':id', pessoa.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="hidden sm:inline">Ver cadastro</span>
                </Link>
              </Button>
            </li>
          ))}
        </ul>
      )}

      {paginas > 1 ? (
        <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-border bg-background pt-3">
          <Button
            variant="outline"
            size="sm"
            disabled={pagina <= 1}
            onClick={() => setPagina((atual) => atual - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Anterior</span>
          </Button>

          <span className="text-sm tabular-nums text-muted-foreground">
            {pagina} de {paginas}
          </span>

          <Button
            variant="outline"
            size="sm"
            disabled={pagina >= paginas}
            onClick={() => setPagina((atual) => atual + 1)}
          >
            <span className="hidden sm:inline">Proxima</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </Modal>
  );
}
